import { useState } from 'react'
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  Tabs,
  Textarea,
  Field,
  Wrap,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiTrash2, FiLink } from 'react-icons/fi'
import { useDeleteAsk, useSaveAsk, useSaveAskTemplate } from '../../hooks/useLobbyDay'
import { MarkdownEditor } from './MarkdownEditor'
import { BillLinkModal } from './BillLinkModal'
import { LinkedBillDetailSheet } from './LinkedBillDetailSheet'
import type { AskBill, AskPosition, LobbyDayAskDto } from '../../types/lobbyDay'

interface AskEditorCardProps {
  eventId: string
  ask?: LobbyDayAskDto     // undefined = create mode
  onDone?: () => void
  /** Create mode only: fired the moment a save-first action (Link bills / Save as template)
   * persists this draft into a real ask, so the parent can stop rendering the freshly-created
   * ask a SECOND time (it now arrives via the invalidated event.asks list). */
  onAskCreated?: (askId: string) => void
}

export function AskEditorCard({ eventId, ask, onDone, onAskCreated }: AskEditorCardProps) {
  const saveMutation = useSaveAsk(eventId)
  const deleteMutation = useDeleteAsk(eventId)
  const saveTemplateMutation = useSaveAskTemplate()

  const [title, setTitle] = useState(ask?.title ?? '')
  const [billNumber, setBillNumber] = useState(ask?.billNumber ?? '')
  const [position, setPosition] = useState<AskPosition>(ask?.position ?? 'Support')
  const [summary, setSummary] = useState(ask?.summary ?? '')
  const [talkingPoints, setTalkingPoints] = useState(ask?.talkingPoints ?? '')
  const [billContent, setBillContent] = useState(ask?.billContent ?? '')
  const [billModalOpen, setBillModalOpen] = useState(false)
  const [linkedBills, setLinkedBills] = useState<AskBill[]>(ask?.linkedBills ?? [])
  // B1: which linked bill's read-only summary/full-text sheet is open (null = closed).
  const [detailBill, setDetailBill] = useState<AskBill | null>(null)
  // Create mode has no `ask` prop yet -- save-first (openBillModal) persists one and remembers
  // it here so "Link bills" works before the explicit "Save ask" button is ever pressed.
  const [createdAsk, setCreatedAsk] = useState<LobbyDayAskDto | undefined>()
  const effectiveAsk = ask ?? createdAsk

  // Persisting a draft into a real ask both remembers it locally (so the same card keeps its
  // modal state) and notifies the parent, which drops the now-duplicate event.asks render.
  const rememberCreatedAsk = (saved: LobbyDayAskDto) => {
    setCreatedAsk(saved)
    onAskCreated?.(saved.id)
  }

  // #2: seed the free-text "Bill" tab from a just-linked bill's summary, but never clobber
  // anything the organizer has already typed there.
  const handleBillLinked = (bill: AskBill) => {
    if (!billContent.trim() && bill.summary) setBillContent(bill.summary)
  }

  const persist = async (): Promise<LobbyDayAskDto> => {
    return saveMutation.mutateAsync({
      askId: effectiveAsk?.id,
      data: {
        title, billNumber: billNumber || null, position, summary: summary || null,
        talkingPoints: talkingPoints || null, billContent: billContent || null,
        sortOrder: effectiveAsk?.sortOrder ?? 0,
      },
    })
  }

  const save = async () => {
    if (!title.trim()) { toaster.create({ type: 'warning', title: 'Title is required' }); return }
    try {
      await persist()
      toaster.create({ type: 'success', title: 'Ask saved' })
      onDone?.()
    } catch {
      toaster.create({ type: 'error', title: 'Failed to save ask' })
    }
  }

  const saveAsTemplate = async () => {
    if (!title.trim()) { toaster.create({ type: 'warning', title: 'Title is required' }); return }
    let target = effectiveAsk
    if (!target) {
      try {
        target = await persist()
        rememberCreatedAsk(target)
      } catch {
        toaster.create({ type: 'error', title: 'Failed to save ask' })
        return
      }
    }
    try {
      await saveTemplateMutation.mutateAsync({ sourceAskId: target.id })
      toaster.create({ type: 'success', title: 'Saved to templates' })
    } catch {
      toaster.create({ type: 'error', title: 'Failed to save template' })
    }
  }

  const openBillModal = async () => {
    if (!title.trim()) { toaster.create({ type: 'warning', title: 'Title is required' }); return }
    if (!effectiveAsk) {
      try {
        const saved = await persist()
        rememberCreatedAsk(saved)
      } catch {
        toaster.create({ type: 'error', title: 'Failed to save ask' })
        return
      }
    }
    setBillModalOpen(true)
  }

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack mb={4} align="flex-end" gap={3}>
        <Field.Root required flex={2}>
          <Field.Label fontSize="sm">Title <Field.RequiredIndicator /></Field.Label>
          <Input size="sm" required value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Safe Staffing Ratios" />
        </Field.Root>
        <Field.Root w="140px">
          <Field.Label fontSize="sm">Bill #</Field.Label>
          <Input size="sm" value={billNumber} onChange={(e) => setBillNumber(e.target.value)} placeholder="SB-214" />
        </Field.Root>
        <Field.Root w="140px">
          <Field.Label fontSize="sm">Position</Field.Label>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={position}
              onChange={(e) => setPosition(e.target.value as AskPosition)}>
              <option value="Support">Support</option>
              <option value="Oppose">Oppose</option>
              <option value="Amend">Amend</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
        <Badge colorPalette={position === 'Support' ? 'green' : position === 'Oppose' ? 'red' : 'yellow'}>
          {position}
        </Badge>
        {ask && (
          <IconButton
            aria-label="Delete ask"
            size="sm"
            variant="ghost"
            colorPalette="red"
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate(ask.id)}><FiTrash2 /></IconButton>
        )}
      </HStack>

      <HStack mb={4} gap={3} align="center" wrap="wrap">
        <Button size="sm" variant="ghost" onClick={openBillModal} disabled={title.trim() === ''}
          loading={saveMutation.isPending && !effectiveAsk}>
          <FiLink /> Link bills
        </Button>
        {linkedBills.length > 0 && (
          <Wrap gap={2}>
            {linkedBills.map((b) => (
              <Button
                key={b.id}
                size="sm"
                variant="outline"
                minH={{ base: '44px', md: 'auto' }}
                onClick={() => setDetailBill(b)}
                aria-label={`View ${b.billNumber} summary`}
              >
                {b.billNumber}
              </Button>
            ))}
          </Wrap>
        )}
        {effectiveAsk && (
          <BillLinkModal
            eventId={eventId}
            askId={effectiveAsk.id}
            open={billModalOpen}
            onClose={() => setBillModalOpen(false)}
            onLinkedBillsChange={setLinkedBills}
            onBillLinked={handleBillLinked}
          />
        )}
      </HStack>

      <LinkedBillDetailSheet bill={detailBill} onClose={() => setDetailBill(null)} />


      <Tabs.Root size="sm" colorPalette="brand" variant='enclosed' defaultValue="talkingPoints">
        <Tabs.List>
          <Tabs.Trigger value="talkingPoints">Talking points</Tabs.Trigger>
          <Tabs.Trigger value="bill">Bill</Tabs.Trigger>
          <Tabs.Trigger value="summary">Summary</Tabs.Trigger>
        </Tabs.List>
        <Tabs.ContentGroup>
          <Tabs.Content value="talkingPoints" px={0}>
            <MarkdownEditor value={talkingPoints} onChange={setTalkingPoints}
              placeholder="- Lead with the staffing-ratio numbers…" />
          </Tabs.Content>
          <Tabs.Content value="bill" px={0}>
            <MarkdownEditor value={billContent} onChange={setBillContent}
              placeholder="Bill text or summary…" rows={12} />
          </Tabs.Content>
          <Tabs.Content value="summary" px={0}>
            <Textarea value={summary} onChange={(e) => setSummary(e.target.value)}
              placeholder="One-paragraph internal summary" rows={3} />
          </Tabs.Content>
        </Tabs.ContentGroup>
      </Tabs.Root>

      <HStack justify="flex-end" mt={3}>
        {onDone && <Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>}
        <Button size="sm" variant="outline" colorPalette="brand" minH="44px" disabled={title.trim() === ''}
          onClick={saveAsTemplate} loading={saveTemplateMutation.isPending}>
          Save as template
        </Button>
        <Button size="sm" colorPalette="brand" minH="44px" disabled={title.trim() === ''}
          onClick={save} loading={saveMutation.isPending}>
          Save ask
        </Button>
      </HStack>
    </Box>
  );
}
